import './App.css';
import React, {Suspense, useEffect} from "react";
import {BrowserRouter as Router, Route, Routes} from 'react-router-dom';
import ReactGA from 'react-ga4';
import * as Sentry from "@sentry/react";
import "./i18n";
import {isMobile} from 'react-device-detect';
import CookieConsentBanner from "./components/CookieConsent/CookieConsentBanner";
import {useCookieConsent} from "./hooks/useCookieConsent";
import SupportWidgetButton from "./components/SupportWidgetButton";

function App() {
    const {analyticsAllowed} = useCookieConsent();

    useEffect(() => {
        if (analyticsAllowed) {
            ReactGA.initialize('G-2W3TS6E710');
        }
    }, [analyticsAllowed]);

    const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

    const LoginPage = React.lazy(() => import("./components/LoginPage/LoginPage"));
    const GameField = React.lazy(() => import("./pages/table/GameField"));
    const MentorCabinet = React.lazy(() => import("./components/mentorCabinet/MentorCabinet"));
    const MentorCabinetMobile = React.lazy(() => import("./components/mentorCabinet/MentorCabinetMobile"));
    const VerifyEmailPage = React.lazy(() => import("./pages/verify_email"));
    const ForgotPassword = React.lazy(() => import("./components/ForgotPassword"));
    const RestPassword = React.lazy(() => import("./components/RestPassword"));
    const Registration = React.lazy(() => import("./components/Registration"));
    const UserConditions = React.lazy(() => import("./static_pages/user_conditions"));
    const ReferralProgramConditions = React.lazy(() => import("./static_pages/referral_program_conditions"));
    const ConfidentialPolicy = React.lazy(() => import("./static_pages/confidential_policy"));
    const AboutRecording = React.lazy(() => import("./static_pages/about_recording"));
    const GroupGameContainer = React.lazy(() => import("./components/GroupGame/GroupGameContainer"));
    const PaymentResult = React.lazy(() => import("./components/mentorCabinet/PaymentResult"));
    const PaymentsPolicy = React.lazy(() => import("./static_pages/payments_policy"));
    const PriceList = React.lazy(() => import("./static_pages/price_list"));
    const GameComments = React.lazy(() => import("./components/mentorCabinet/GameComments"));
    const GameReport = React.lazy(() => import("./components/report/GameReport"));
    const ReportPaymentResult = React.lazy(() => import("./components/report/ReportPaymentResult"));
    const GroupGameComments = React.lazy(() => import("./components/mentorCabinet/GroupGameComments"));
    const Mentors = React.lazy(() => import("./components/Mentors"));
    const PriceList_en = React.lazy(() => import("./static_pages/price_list_en"));
    const Payments_policy_en = React.lazy(() => import("./static_pages/payments_policy_en"));
    const UserConditions_en = React.lazy(() => import("./static_pages/user_conditions_en"));
    const Confidential_policy_en = React.lazy(() => import("./static_pages/confidential_policy_en"));
    const ReferralProgramConditions_en = React.lazy(() => import("./static_pages/referral_program_conditions_en"));
    const AboutRecording_en = React.lazy(() => import("./static_pages/about_recording_en"));
    const StartPageNew2_ua = React.lazy(() => import("./components/StartPageNew2_ua"))
    const StartPageNew2_en = React.lazy(() => import("./components/StartPageNew2_en"))
    const FiledStylesPreview_en = React.lazy(() => import("./static_pages/field_styles_en"));
    const FiledStylesPreview = React.lazy(() => import("./static_pages/field_styles"));
    const FiledStylesPreview_en_mobile = React.lazy(() => import("./static_pages/field_styles_en_mobile"));
    const FiledStylesPreview_mobile = React.lazy(() => import("./static_pages/field_styles_mobile"));
    const DigitalProductPolicy_ua = React.lazy(() => import("./static_pages/digital_products_policy_ua"));
    const DigitalProductPolicy_en = React.lazy(() => import("./static_pages/digital_products_policy_en"));
    const Impressum = React.lazy(() => import("./static_pages/impressum"));
    const Datenschutz = React.lazy(() => import("./static_pages/datenschutz"));

    // New landing redesign, built in its own package (src/redesign) under /redesign
    // so it can be iterated on without touching the existing "/" and "/en" landing.
    const RedesignHomePage = React.lazy(() => import("./redesign/pages/HomePage"));
    const RedesignForGuidesPage = React.lazy(() => import("./redesign/pages/ForGuidesPage"));
    const RedesignPricingPage = React.lazy(() => import("./redesign/pages/PricingPage"));
    const RedesignOnboardingPage = React.lazy(() => import("./redesign/pages/OnboardingPage"));
    const RedesignFindGuidePage = React.lazy(() => import("./redesign/pages/FindGuidePage"));
    const RedesignMatchPage = React.lazy(() => import("./redesign/pages/MatchPage"));

    function importDeps() {
        import('./components/Variables.css') //
        // import("./pages/table/GameField") //
        // import("./components/LoginPage/LoginPage") //
        // import("./components/mentorCabinet/MentorCabinet"); //
        // import("./pages/verify_email"); //
        // import("./components/ForgotPassword"); //
        // import("./components/RestPassword"); //
        // import("./components/Registration"); //
        // import("./static_pages/user_conditions"); //
        // import("./static_pages/referral_program_conditions"); //
        // import("./static_pages/confidential_policy"); //
        // import("./static_pages/about_recording"); //
        // import("./static_pages/field_styles"); //
        // import("./components/GroupGame/GroupGameContainer"); //
        // import("./components/mentorCabinet/PaymentResult"); //
        // import("./static_pages/payments_policy"); //
        // import("./static_pages/price_list"); //
        // import("./components/mentorCabinet/GameComments"); //
        // import("./components/mentorCabinet/GroupGameComments"); //
        // import("./components/Mentors"); //
        // import("./static_pages/price_list_en"); //
        // import("./static_pages/payments_policy_en"); //
        // import("./static_pages/user_conditions_en"); //
        // import("./static_pages/confidential_policy_en"); //
        // import("./static_pages/referral_program_conditions_en"); //
        // import("./static_pages/about_recording_en"); //
        // import("./components/StartPageNew2_ua"); //
        // import("./components/StartPageNew2_en"); //
        // import("./static_pages/field_styles_en"); //

    }

    useEffect(() => {
        importDeps()
    } , [])

    return (
        <Router>
            <Suspense fallback={<div style={{
                position: "absolute",
                left: "45vw",
                top: "45vh",
                fontSize: "30px",
                fontWeight: "bold"
            }}>Loading...</div>}>
                <SentryRoutes>
                    <Route path="/register" element={<Registration/>}/>
                    <Route path="/login" element={<LoginPage/>}/>
                    <Route path="/game/:id" element={<GameField/>}/>
                    <Route path="/groupgame/:id" element={<GroupGameContainer/>}/>
                    <Route path="/mentor/cabinet" element={isMobile ? <MentorCabinetMobile/> :<MentorCabinet/>}/>
                    <Route path={"/verify_email"} element={<VerifyEmailPage/>}/>
                    <Route path={"/forgot_password"} element={<ForgotPassword/>}/>
                    <Route path={"/reset_password"} element={<RestPassword/>}/>
                    <Route path={"/agreement"} element={<UserConditions/>}/>
                    <Route path={"/agreement_en"} element={<UserConditions_en/>}/>
                    <Route path={"/confidential_policy"} element={<ConfidentialPolicy/>}/>
                    <Route path={"/confidential_policy_en"} element={<Confidential_policy_en/>}/>
                    <Route path={"/referral_agreement"} element={<ReferralProgramConditions/>}/>
                    <Route path={"/referral_agreement_en"} element={<ReferralProgramConditions_en/>}/>
                    <Route path={"/about_recording"} element={<AboutRecording/>}/>
                    <Route path={"/about_recording_en"} element={<AboutRecording_en/>}/>
                    <Route path={"/about_payments"} element={<PaymentsPolicy/>}/>
                    <Route path={"/about_payments_en"} element={<Payments_policy_en/>}/>
                    <Route path={"/preview_field_styles"} element={isMobile ? <FiledStylesPreview_mobile/> : <FiledStylesPreview/>}/>
                    <Route path={"/preview_field_styles_en"} element={isMobile ? <FiledStylesPreview_en_mobile/> : <FiledStylesPreview_en/>}/>
                    <Route path={"/game_comments/:game_id"} element={<GameComments/>}/>
                    <Route path={"/game/:id/report"} element={<GameReport/>}/>
                    <Route path={"/report_payment_return"} element={<ReportPaymentResult/>}/>
                    <Route path={"/group_game_comments/:game_id/:player_id"} element={<GroupGameComments/>}/>
                    <Route path={"/stripe_payment_return"} element={<PaymentResult/>}/>
                    <Route path={"/price"} element={<PriceList/>}/>
                    <Route path={"/price_en"} element={<PriceList_en/>}/>
                    <Route path={"/mentors"} element={<Mentors/>}/>
                    <Route path={"/digital_products_policy_ua"} element={<DigitalProductPolicy_ua/>}/>
                    <Route path={"/digital_products_policy_en"} element={<DigitalProductPolicy_en/>}/>
                    <Route path={"/impressum"} element={<Impressum/>}/>
                    <Route path={"/datenschutz"} element={<Datenschutz/>}/>

                    {/* New main landing (src/redesign) — replaces the old "/" and "/en" landing,
                        which stays in the codebase (StartPageNew2_ua/_en, imported above) but is
                        no longer routed anywhere, per explicit instruction not to delete it yet. */}
                    <Route path={"/"} element={<RedesignHomePage lang="ua"/>}/>
                    <Route path={"/en"} element={<RedesignHomePage lang="en"/>}/>
                    <Route path={"/guides"} element={<RedesignForGuidesPage lang="ua"/>}/>
                    <Route path={"/en/guides"} element={<RedesignForGuidesPage lang="en"/>}/>
                    <Route path={"/pricing"} element={<RedesignPricingPage lang="ua"/>}/>
                    <Route path={"/en/pricing"} element={<RedesignPricingPage lang="en"/>}/>
                    <Route path={"/onboarding"} element={<RedesignOnboardingPage lang="ua"/>}/>
                    <Route path={"/en/onboarding"} element={<RedesignOnboardingPage lang="en"/>}/>
                    <Route path={"/find-guide"} element={<RedesignFindGuidePage lang="ua"/>}/>
                    <Route path={"/en/find-guide"} element={<RedesignFindGuidePage lang="en"/>}/>
                    <Route path={"/match"} element={<RedesignMatchPage lang="ua"/>}/>
                    <Route path={"/en/match"} element={<RedesignMatchPage lang="en"/>}/>
                </SentryRoutes>
            </Suspense>
            <CookieConsentBanner/>
            <SupportWidgetButton/>
        </Router>
    );
}

export default App;
